import isSequenceActive from "Utils/sequenceActive";
import { hasLinkedInSteps } from "Pages/Dashboard/sequences/SequencePage/sequenceUtils";

const DAY = 86400000;
const domainOf = email => String(email || "").split("@")[1] || "";
const dnsFailing = d => !d ? false : !d.spfRecord || !d.dmarcRecord || !d.dkim;

const RULES = {
  account: ({ inboxes = [], liAccounts, sequences, now = Date.now(), actions = {} }) => {
    const out = [];
    const live = [];
    (inboxes || []).forEach(i => {
      // Disconnected stays per-inbox: each one is separately actionable and a
      // workspace rarely has more than one or two.
      // Real statuses are ACTIVE / INACTIVE / ACCESS_REVOKED / DELETED --
      // "DISCONNECTED" is never emitted, so this rule never fired.
      if (i.status === "ACCESS_REVOKED" || i.accessRevokedBySystem === true || i.disconnected) {
        out.push({ tone: "bad", key: "disconnected", label: `${i.email} is disconnected`, text: "Sequences on it are stalled. Reconnect." });
        return;
      }
      live.push(i);
    });

    /*
     * The remaining inbox rules are ROLLED UP. One finding per inbox per rule
     * fanned out until three inboxes with warmup off filled the five-finding
     * cap with the same sentence three times, hiding credits, schedules,
     * unsubscribe and seller-profile findings entirely. Same fix as the
     * low-yield enrichment nudge: name the count, keep one card.
     */
    const rollUp = (matches, key, tone, one, many, text) => {
      if (!matches.length) return;
      out.push({
        tone,
        key,
        label: matches.length === 1 ? one(matches[0]) : many(matches),
        text
      });
    };

    const age = i => (i.connectedTs ? Math.floor((now - i.connectedTs) / DAY) : 999);
    rollUp(
      live.filter(i => age(i) < 14 && i.dailyLimit > 100),
      "new_inbox_high_limit",
      "warn",
      i => `${i.email} sends ${i.dailyLimit}/day on a ${age(i)}-day-old inbox`,
      m => `${m.length} new inboxes send over 100/day`,
      "New inboxes get flagged above ~50/day. Lower it for two weeks."
    );
    rollUp(
      live.filter(i => i.signature !== undefined && !i.signature),
      "no_signature",
      "neutral",
      i => `${i.email} has no signature`,
      m => `${m.length} inboxes have no signature`,
      "Replies drop without a real name and company."
    );
    const warming = live.filter(
      i => i.warmingEnabled && typeof i.overallEmailMetrics?.inboxPercentage === "number" && i.overallEmailMetrics.inboxPercentage < 100
    );
    if (warming.length) {
      const pct = Math.min(...warming.map(i => i.overallEmailMetrics.inboxPercentage));
      out.push({
        tone: "warn",
        key: "inbox_warming",
        label: warming.length === 1 ? `${warming[0].email} is still warming` : `${warming.length} inboxes are still warming`,
        text: `${pct}% warmed. Sending at full volume now risks spam placement.`,
        actions: actions.inboxWarming ? [{ label: "Show warmup progress", run: actions.inboxWarming }] : []
      });
    }

    const failingDomains = [...new Set(inboxes.filter(i => dnsFailing(i.domainHealth?.dnsRecords)).map(i => domainOf(i.email)).filter(Boolean))];
    if (failingDomains.length) {
      out.push({
        tone: "bad",
        key: "auth_failing",
        label: failingDomains.length === 1 ? `Authentication failing on ${failingDomains[0]}` : `Authentication failing on ${failingDomains.length} domains`,
        text: "SPF, DKIM or DMARC is failing; providers will reject you.",
        actions: actions.authFailing ? [{ label: "What do I fix?", run: () => actions.authFailing(failingDomains[0]) }] : []
      });
    }

    const liDisconnected = (liAccounts || []).some(a => a.status !== "ACTIVE");
    if (liDisconnected && (sequences === undefined || (sequences || []).some(hasLinkedInSteps))) {
      out.push({
        tone: "bad",
        key: "linkedin_disconnected",
        label: "LinkedIn disconnected",
        text: "LinkedIn steps will not run until you reconnect.",
        actions: actions.linkedinDisconnected ? [{ label: "Show affected sequences", run: actions.linkedinDisconnected }] : []
      });
    }

    const activeInboxes = inboxes.filter(i => i.status === "ACTIVE");
    const activeSequences = Array.isArray(sequences) ? sequences.filter(isSequenceActive) : [];
    if (activeInboxes.length === 1 && Array.isArray(sequences) && activeSequences.length >= 2) {
      out.push({
        tone: "neutral",
        key: "single_inbox",
        label: "Single sending inbox",
        text: "Two or more active sequences share one inbox.",
        actions: actions.singleInbox ? [{ label: "Why more inboxes help", run: actions.singleInbox }] : []
      });
    }
    return out;
  },
  creditUsage: ({ credits = null, creditCycle, now = Date.now(), actions = {} }) => {
    const out = credits !== null && credits < 50
      ? [{ tone: "warn", key: "low_credits", label: `${credits} credits left`, text: "Enrichment and reveals stop at zero." }]
      : [];
    const { used, total, cycleStart, cycleEnd } = creditCycle || {};
    if ([used, total, cycleStart, cycleEnd].every(v => typeof v === "number") && total > 0 && cycleEnd > cycleStart) {
      const usedShare = used / total;
      const timeLeftShare = (cycleEnd - now) / (cycleEnd - cycleStart);
      if (usedShare >= 0.8 && timeLeftShare >= 0.25) {
        out.push({
          tone: "neutral",
          key: "credits_early",
          label: "Credits running out early",
          text: `${Math.round(usedShare * 100)}% used with ${Math.round(timeLeftShare * 100)}% of the cycle left.`,
          actions: actions.creditsEarly ? [{ label: "Show usage breakdown", run: actions.creditsEarly }] : []
        });
      }
    }
    return out;
  },
  billing: ({ credits = null, creditCycle, now, actions }) => RULES.creditUsage({ credits, creditCycle, now, actions }),
  schedules: ({ schedules }) => {
    const out = Array.isArray(schedules) && !schedules.length
      ? [{ tone: "warn", key: "no_schedule", label: "No sending schedule", text: "Without one, emails go out at 3am in the prospect's timezone." }]
      : [];
    (schedules || [])
      .filter(s => (s.days || []).some(d => /^(sat|sun)/i.test(String(d))))
      .forEach(s => out.push({ tone: "warn", key: "weekend_schedule", label: `${s.name || "A schedule"} sends on weekends`, text: "Weekend cold email gets buried by Monday. Keep it to weekdays." }));
    return out;
  },
  unsubscribe: ({ unsubscribeText }) =>
    unsubscribeText !== undefined && !unsubscribeText
      ? [{ tone: "bad", key: "no_unsubscribe_text", label: "Unsubscribe text not set", text: "Cold email needs it. Set it once here." }]
      : [],
  crm: ({ lastSyncFailed = false }) =>
    lastSyncFailed ? [{ tone: "bad", key: "sync_failed", label: "Last CRM sync failed", text: "Records are drifting. Check the sync log." }] : [],
  "seller-profiles": ({ profiles = [] }) =>
    !(profiles || []).length
      ? [{ tone: "warn", key: "no_profile", label: "No seller profile", text: "Deep research and drafts are generic without one." }]
      : [],
  "domain-health-center": ({ dnsRecords, actions = {} }) => {
    if (!dnsRecords || !Object.keys(dnsRecords).length || !dnsFailing(dnsRecords)) return [];
    const domain = domainOf(dnsRecords.email);
    return [{
      tone: "bad",
      key: "auth_failing",
      label: `Authentication failing on ${domain || "your domain"}`,
      text: "SPF, DKIM or DMARC is failing; providers will reject you.",
      actions: actions.authFailing ? [{ label: "What do I fix?", run: () => actions.authFailing(domain) }] : []
    }];
  },
  doNotContact: ({ domains }) =>
    Array.isArray(domains) && !domains.length
      ? [{ tone: "neutral", key: "no_blocked_domains", label: "No blocked domains", text: "Add competitors and customers so sequences never hit them." }]
      : []
};

// subPage: the segment after /settings/, e.g. "account", "creditUsage".
const settingsFindings = (subPage = "", data = {}) => (RULES[subPage] ? RULES[subPage](data) : []);

export default settingsFindings;
